import { AssignedModuleData } from "../interface";
import { ActionEntityEnum, StorageKeyEnum } from "./enum";
import { IsNullOrEmptyArray, IsStringNullEmptyOrUndefined } from "./null-check";
import { allFeaturedModules } from "./constants";
import { Dispatch, SetStateAction } from "react";

export const handleDateTimeFormat = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};

const GOLDEN_ANGLE = 137.508;

const LIGHTNESS_LEVELS = [38, 48, 58, 68];

export const generateOfficeColor = (officeId: string) => {
    let hash = 0;
    for (let i = 0; i < officeId.length; i++) {
        hash = (hash * 31 + officeId.charCodeAt(i)) >>> 0;
    }

    // Evenly spread hues
    let hue = (hash * GOLDEN_ANGLE) % 360;
    if (hue >= 10 && hue <= 50) {
        hue = (hue + 120) % 360;
    }
    // Pick clearly different lightness (not tiny delta)
    const lightness = LIGHTNESS_LEVELS[hash % LIGHTNESS_LEVELS.length];

    // Slight saturation variation but still strong
    const saturation = 75 + (hash % 15); // 75–90

    return {
        fill: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
        stroke: `hsl(${hue}, ${saturation}%, ${Math.max(lightness - 25, 15)}%)`
    };
};

export const isImpersonatedUser = (): boolean => {
    try {
        const userInfo = JSON.parse(
            localStorage.getItem(StorageKeyEnum.JB_GLASS_USER_INFO) || "{}"
        );

        return userInfo?.isImpersonatedUser || false;
    } catch (error) {
        return false;
    }
};

export const handleNavigationAfterSuccessfulSignIn = (
    assignedModule: AssignedModuleData[],
    setIsLoading?: Dispatch<SetStateAction<boolean>>
): void => {

    const dashboardExist = assignedModule.filter(
        items =>
            items.moduleName &&
            items.moduleName.toLowerCase() === allFeaturedModules.dashboard.toLowerCase() &&
            items.action === ActionEntityEnum.LIST
    );

    let navigatePageIfWaltzOrderNotExists = "";

    if (IsNullOrEmptyArray(dashboardExist)) {

        const listPermissions: AssignedModuleData[] =
            assignedModule.filter(items => items.action === ActionEntityEnum.LIST);

        const permission: AssignedModuleData = listPermissions[0];

        switch (permission.moduleName) {

            case "Downloads":
                navigatePageIfWaltzOrderNotExists = "/downloads";
                break;

            case "Analytics":
                navigatePageIfWaltzOrderNotExists = "/analytics";
                break;

            case "Settings":
                navigatePageIfWaltzOrderNotExists = "/settings";
                break;

            case "Waltz Orders":
                navigatePageIfWaltzOrderNotExists = "/wall-orders";
                break;

            default:
                break;
        }
    }

    setTimeout(() => {

        if (setIsLoading) {
            setIsLoading(false);
        }

        window.location.href =
            IsNullOrEmptyArray(dashboardExist)
                ? navigatePageIfWaltzOrderNotExists
                : "/dashboard";

    }, 100);
};


export const stripDisplayName = (value: string | null | undefined, displayName: string | null | undefined): string => {
    if (!value) return "";
    if (!displayName || IsStringNullEmptyOrUndefined(displayName)) return value;
    const trimmedDisplay = displayName.trim();
    const trimmedValue = value.trim();
    if (trimmedValue.toLowerCase().startsWith(trimmedDisplay.toLowerCase())) {
        return trimmedValue.slice(trimmedDisplay.length).replace(/^[,\s]+/, "").trim();
    }
    return trimmedValue;
};

//remove addressline1 details from addressline2
export const removeAddressBoxValuesFromAddressLine = (
    value: string | null | undefined,
    boxValues: (string | number | null | undefined)[]
): string => {
    if (!value) return "";

    const normalize = (text: string) => text.toLowerCase().replace(/[\s\-.]/g, "");

    const boxValueSet = new Set<string>();
    boxValues.forEach((boxValue) => {
        if (boxValue === null || boxValue === undefined) return;
        String(boxValue).split(",").forEach((piece) => {
            const normalized = normalize(piece);
            if (normalized) boxValueSet.add(normalized);
        });
    });

    if (boxValueSet.size === 0) return value.trim();

    const seen = new Set<string>();
    return value
        .split(",")
        .map((part) => part.trim())
        .filter((part) => {
            const normalized = normalize(part);
            if (!normalized || boxValueSet.has(normalized) || seen.has(normalized)) return false;
            seen.add(normalized);
            return true;
        })
        .join(", ");
};

/** selected ids first, the rest in their original order, so an opened multi select shows what is already picked on top */
export const sortSelectedFirst = <T>(options: T[], selectedIds: string[], keyOf: (item: T) => string): T[] => {
    if (IsNullOrEmptyArray(selectedIds)) {
        return options;
    }
    return [
        ...options.filter((item) => selectedIds.includes(keyOf(item))),
        ...options.filter((item) => !selectedIds.includes(keyOf(item)))
    ];
};
